/**
 * ImportLogsButton — bulk CSV import (bonus G).
 *
 * Opens a file picker, calls decodeLogsFromCsv(), deduplicates,
 * merges with existing logs, and shows a summary.
 */
import { useRef, useState } from 'react';
import { decodeLogsFromCsv } from '@reefapp/shared';
import type { TestLogEntry } from '@reefapp/shared';

export interface ImportLogsButtonProps {
  existingLogs: ReadonlyArray<TestLogEntry>;
  onImport(merged: TestLogEntry[]): void;
}

export function ImportLogsButton({
  existingLogs,
  onImport,
}: ImportLogsButtonProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState<{
    imported: number;
    dup: number;
    errors: number;
    errorMessages: string[];
  } | null>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const csv = reader.result as string;
      const { entries, errors } = decodeLogsFromCsv(csv);

      // Build a lookup of existing entries for dedup
      const existingKeys = new Set(
        existingLogs.map(
          (l) => `${l.date}|${l.kitSlug}|${l.parameter}|${l.result}`,
        ),
      );

      const newEntries: TestLogEntry[] = [];
      let dup = 0;
      for (const entry of entries) {
        const key = `${entry.date}|${entry.kitSlug}|${entry.parameter}|${entry.result}`;
        if (existingKeys.has(key)) {
          dup++;
        } else {
          newEntries.push(entry);
        }
      }

      if (
        newEntries.length === 0 &&
        !window.confirm('No new entries to import. Continue?')
      ) {
        return;
      }

      if (
        newEntries.length > 0 &&
        !window.confirm(`Import ${newEntries.length} log entries? Existing entries will not be deleted.`)
      ) {
        return;
      }

      onImport([...existingLogs, ...newEntries]);
      setImportResult({
        imported: newEntries.length,
        dup,
        errors: errors.length,
        errorMessages: errors,
      });
    };
    reader.readAsText(file);
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        onClick={() => fileRef.current?.click()}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-slate-700 text-slate-200 hover:bg-slate-600 transition-colors"
      >
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        Import CSV
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => void handleFile(e)}
      />
      {importResult && (
        <div className="ml-2 text-xs text-slate-400">
          Imported {importResult.imported}, {importResult.dup} skipped as
          duplicates, {importResult.errors} errors.
          {importResult.errorMessages.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-cyan-400 hover:text-cyan-300">
                View errors
              </summary>
              <ul className="list-disc pl-4 mt-1 text-red-400">
                {importResult.errorMessages.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
